import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Table } from 'reactstrap';
import Breadcrumb from 'components/App/Breadcrumb';
import Meta from 'components/App/Meta';
import Loading from 'components/App/Loading';
import { LOCATIONS } from 'constants/index';
import {
  actionGetVatSettingList,
  actionCreateVatSetting,
  actionUpdateVatSetting,
} from 'store/actions';
import cn from 'utils/classnames';

import dayjs from 'dayjs';
import ModalAddEditTax from './ModalAddEditTax';
import styles from './ManagerTax.module.scss';

const PAGE_TITLE = 'QUẢN LÝ THUẾ';

const breadcrumbList = [
  {
    name: 'Quản lý thuế',
    isActive: true,
    link: LOCATIONS.MANAGER_TAX,
  },
];

function ManagerTax() {
  const dispatch = useDispatch();
  const {
    vatSettingList,
    isLoadingVatSettingList,
    isLoadingCreateVatSetting,
    isLoadingUpdateVatSetting,
  } = useSelector(
    (state) => state.VatSetting || {},
  );

  const [vatList, setVatList] = useState([]);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [editingVat, setEditingVat] = useState(null);

  useEffect(() => {
    dispatch(actionGetVatSettingList());
  }, [dispatch]);

  const formatDateDisplay = useCallback((start, end) => {
    const formatSingle = (dStr) => {
      if (!dStr) return '';
      const dObj = dayjs(dStr);
      if (dObj.isValid()) {
        return dObj.format('DD/MM/YYYY');
      }
      return dStr;
    };

    const s = formatSingle(start);
    const e = formatSingle(end);

    if (s && e) {
      return `${s} – ${e}`;
    }
    if (s) {
      return `${s} -`;
    }
    return 'Từ hôm nay -';
  }, []);

  useEffect(() => {
    let listArray = [];
    if (Array.isArray(vatSettingList)) {
      listArray = vatSettingList;
    } else if (Array.isArray(vatSettingList?.data)) {
      listArray = vatSettingList.data;
    } else if (Array.isArray(vatSettingList?.payload?.data)) {
      listArray = vatSettingList.payload.data;
    }

    const formattedFromApi = listArray.map((item, idx) => {
      const status = item.status || (item.isApplied ? 'APPLIED' : 'STOPPED');
      const statusUpper = String(status).toUpperCase();
      return {
        id: item._id || item.id || `vat-api-${idx}`,
        name: item.name || `Vietnam VAT ${item.value}%`,
        rate: item.value,
        startDate: item.startAt ? dayjs(item.startAt).format('YYYY-MM-DD') : '',
        endDate: item.endAt ? dayjs(item.endAt).format('YYYY-MM-DD') : '',
        applyTime: formatDateDisplay(item.startAt, item.endAt),
        status,
        canEdit: statusUpper !== 'STOPPED' && statusUpper !== 'DISABLED',
      };
    });
    setVatList(formattedFromApi);
  }, [vatSettingList, formatDateDisplay]);

  const renderStatusBadge = useCallback((status) => {
    const s = String(status || '').toUpperCase();
    if (s === 'APPLIED') {
      return <span className={styles.StatusApplied}>Đang áp dụng</span>;
    }
    if (s === 'UPCOMING') {
      return <span className={styles.StatusUpcoming}>Chưa diễn ra</span>;
    }
    return <span className={styles.StatusStopped}>Ngưng áp dụng</span>;
  }, []);

  const handleOpenAddModal = useCallback(() => {
    setEditingVat(null);
    setIsOpenModal(true);
  }, []);

  const handleOpenEditModal = useCallback((vatItem) => {
    setEditingVat(vatItem);
    setIsOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsOpenModal(false);
    setEditingVat(null);
  }, []);

  const handleSubmitVat = useCallback(
    (taxData) => {
      const apiPayload = {
        name: taxData.name,
        value: String(taxData.rate || taxData.value || '0'),
        startAt: taxData.startAt || taxData.startDate,
        ...(taxData.endAt || taxData.endDate ? { endAt: taxData.endAt || taxData.endDate } : {}),
      };

      const handleSuccess = () => {
        dispatch(actionGetVatSettingList());
        handleCloseModal();
      };

      if (taxData.id) {
        dispatch(
          actionUpdateVatSetting({
            id: taxData.id,
            values: apiPayload,
            callback: handleSuccess,
          }),
        );
      } else {
        dispatch(
          actionCreateVatSetting({
            values: apiPayload,
            callback: handleSuccess,
          }),
        );
      }
    },
    [dispatch, handleCloseModal],
  );

  return (
    <div className={cn('page-content', styles.Page)}>
      <Meta name={PAGE_TITLE} />
      {(isLoadingVatSettingList || isLoadingCreateVatSetting || isLoadingUpdateVatSetting) && (
        <Loading loading loaderOnPage />
      )}

      <Container fluid>
        <Breadcrumb pageTitle={PAGE_TITLE} breadcrumbList={breadcrumbList} />

        <div className={styles.CardContainer}>
          <div className={styles.CardHeader}>
            <h3 className={styles.Title}>Quản lý thuế GTGT (VAT)</h3>
            <button
              type="button"
              className={styles.AddButton}
              onClick={handleOpenAddModal}
            >
              <span className={styles.PlusIcon}>+</span>
              <span>Thêm VAT mới</span>
            </button>
          </div>

          <Table responsive className={styles.TaxTable}>
            <thead>
              <tr>
                <th style={{ width: '30%' }}>VAT</th>
                <th style={{ width: '30%' }}>Thời gian áp dụng</th>
                <th style={{ width: '25%' }}>Trạng thái</th>
                <th style={{ width: '15%' }}>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {vatList.length > 0 ? (
                vatList.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {item.name && (item.rate ?? item.value) !== undefined && (item.rate ?? item.value) !== null && !String(item.name).includes(`${item.rate ?? item.value}%`)
                        ? `${item.name} ${item.rate ?? item.value}%`
                        : item.name || ((item.rate ?? item.value) != null ? `${item.rate ?? item.value}%` : '')}
                    </td>
                    <td>{item.applyTime}</td>
                    <td>{renderStatusBadge(item.status)}</td>
                    <td>
                      {item.canEdit ? (
                        <button
                          type="button"
                          className={styles.BtnEdit}
                          onClick={() => handleOpenEditModal(item)}
                        >
                          Sửa
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center py-4 text-muted">
                    Chưa có dữ liệu thuế GTGT
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Container>

      {/* Modal Popup Thêm / Sửa thuế GTGT */}
      <ModalAddEditTax
        isOpen={isOpenModal}
        onToggle={handleCloseModal}
        onSubmit={handleSubmitVat}
        itemData={editingVat}
      />
    </div>
  );
}

export default ManagerTax;
